import React, { useEffect, useState } from 'react';
import apiService from './api';
import { Box, Typography } from '@mui/material';
import '../components/Surveys/SurveyPopup.css';

const downarrow = apiService.getBlobAssetUrl('Dropdown_3.svg');
const arrowicon = apiService.getBlobAssetUrl('ForwardArrow.svg');

const SurveyDetailsTemplate = ({
  uniqueId,
  userResponses,
  onResponseChange,
  setSurveyData,
  setQuestionTypes,
  surveyData,
  questionTypes,
  unansweredQuestions = [],
  firstUnansweredQuestionId,
  onAlreadySubmitted,
  currentSectionIndex = 0,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    console.log('surveyData updated in parent:', surveyData);
    const fetchSurveyDetails = async () => {
      try {
        setLoading(true);
        const questionTypesData = await apiService.fetchData('questions_types');
        const fetchedQuestionTypes = Array.isArray(questionTypesData.results) ? questionTypesData.results : [];
        setQuestionTypes(fetchedQuestionTypes);

        const data = await apiService.fetchData(`surveys/${uniqueId}`);
        const survey = data?.results?.[0] || {};
        console.log("Fetched survey:", survey);
        if (survey.IsRespCompleted === true) {
          if (typeof onAlreadySubmitted === 'function') {
            onAlreadySubmitted();
          }
          setLoading(false);
          return;
        }

        const formattedSections = mapSections(survey.Sections || [], fetchedQuestionTypes);

        const fullSurvey = {
          name: survey.Name || 'N/A',
          sections: formattedSections,
        };

        setSurveyData(fullSurvey);

        // Expand sections by default and ensure the section with the first unanswered question is expanded
        const initialExpanded = formattedSections.reduce((acc, section) => ({
          ...acc,
          [section.sectionId]: true,
        }), {});
        setExpandedSections(initialExpanded);

        setLoading(false);
      } catch (err) {
        console.error(err);
        setError('Failed to load survey details.');
        setLoading(false);
      }
    };

    if (uniqueId) {
      fetchSurveyDetails();
    }
  }, [uniqueId, setSurveyData, setQuestionTypes]);

  const mapQuestions = (questions, questionTypes) =>
    questions.map(question => {
      const questionTypeObj = questionTypes.find(type => type.ID === question.QuestionType);
      const questionType = questionTypeObj?.Type?.toLowerCase() || 'multiple choice';

      return {
        questionId: question.QuestionID,
        text: question.QuestionText || '',
        options: Array.isArray(question.Options)
          ? question.Options.map(opt => ({
            value: opt.OptionText || '',
            isDefault: opt.IsDefault || false,
            optionId: opt.OptionID,
            weightage: opt.Weightage || 0,
          }))
          : [],
        questionType,
        addCommentBox: question.QuestionComments || false,
        userResponse: question.Paragraph?.[0]?.ParagraphText || '',
        min: question.Scale?.MinValue || '',
        max: question.Scale?.MaxValue || '',
        minLabel: question.Scale?.MinValueLabel || '',
        maxLabel: question.Scale?.MaxValueLabel || '',
      };
    });

  const mapMeasures = (subsections, questionTypes) =>
    subsections.map(measure => ({
      measureId: measure.SubSectionID,
      measureName: measure.SubSectionName || 'Untitled Measure',
      measureDescription: '',
      questions: Array.isArray(measure.Questions)
        ? mapQuestions(measure.Questions, questionTypes)
        : [],
    }));

  const mapSections = (sections, questionTypes) =>
    sections.map(section => ({
      sectionId: section.SectionID,
      title: section.SectionName || 'Untitled Section',
      description: section.SectionDescription || '',
      weightage: section.SectionWeightage || '',
      subsections: Array.isArray(section.SubSections)
        ? mapMeasures(section.SubSections, questionTypes)
        : [],
    }));

  const sectionContainsQuestion = (section, questionId) => {
    return Array.isArray(section.subsections) &&
      section.subsections.some(sub =>
        Array.isArray(sub.questions) &&
        sub.questions.some(q => q.questionId === questionId)
      );
  };

  useEffect(() => {
    if (firstUnansweredQuestionId && surveyData) {
      const sectionToExpand = surveyData.sections.find(section =>
        sectionContainsQuestion(section, firstUnansweredQuestionId)
      );

      if (sectionToExpand && !expandedSections[sectionToExpand.sectionId]) {
        setExpandedSections(prev => ({
          ...prev,
          [sectionToExpand.sectionId]: true,
        }));
      }
    }
  }, [firstUnansweredQuestionId, surveyData, expandedSections]);

  const toggleSectionVisibility = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const renderCommentBox = (question) => (
    <div className="survey-form-form-group">
      <textarea
        placeholder="Enter comment..."
        className="survey-form-comment-input"
        style={{
          width: '100%',
          padding: '8px',
          border: '1px solid #013E7D4D',
          borderRadius: '4px',
          minHeight: '100px',
          resize: 'vertical',
          fontFamily: 'Mulish, sans-serif'
        }}
        value={userResponses[question.questionId]?.comment || ''}
        onChange={(e) => onResponseChange(question.questionId, e.target.value, 'comment')}
      />
    </div>
  );

  const renderOptions = (question) => {
    const response = userResponses[question.questionId]?.response;
    const handleCheckboxChange = (e) => {
      const currentResponses = response || [];
      const newResponses = e.target.checked
        ? [...currentResponses, e.target.value]
        : currentResponses.filter(val => val !== e.target.value);

      onResponseChange(question.questionId, newResponses);
    };

    if (question.questionType === 'dropdown') {
      return (
        <div className="survey-form-dropdown-wrapper" style={{ width: '100%', paddingLeft: '10px' }}>
          <select
            value={response || ''}
            onChange={(e) => onResponseChange(question.questionId, e.target.value)}
            className="survey-form-dropdown-select"
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: '#fff',
              fontSize: '14px',
              height: '40px',
              border: '1px solid #013E78'
            }}
          >
            <option value="" disabled>Select an option</option>
            {question.options.map((option, index) => (
              <option key={index} value={option.value} title={`Weightage: ${option.weightage}`}>
                {option.value} (Weightage: {option.weightage})
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div className="survey-form-options-list" style={{ width: '100%' }}>
        {question.options?.length > 0 ? (
          question.options.map((option, optionIndex) => (
            <div
              key={optionIndex}
              className={
                question.questionType === 'multiple choice'
                  ? 'survey-form-radio-group'
                  : 'survey-form-checkbox-group'
              }
              style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}
            >
              {question.questionType === 'multiple choice' && (
                <input
                  type="radio"
                  name={`question-${question.questionId}`}
                  value={option.value}
                  checked={response === option.value}
                  onChange={(e) => onResponseChange(question.questionId, e.target.value)}
                  style={{ marginRight: '8px' }}
                />
              )}
              {question.questionType === 'checkboxes' && (
                <input
                  type="checkbox"
                  value={option.value}
                  checked={response?.includes(option.value) || false}
                  onChange={handleCheckboxChange}
                  style={{ marginRight: '8px' }}
                />
              )}
              <input
                type="text"
                value={option.value}
                readOnly
                className="survey-details-option-input"
                style={{ flex: 1, marginRight: '8px', width: '700px' }}
              />
              <input
                type="text"
                value={option.weightage}
                readOnly
                className="survey-details-weightage-input"
                style={{ width: '20px', padding: '8px', border: '1px solid #013E7D4D', borderRadius: '4px', backgroundColor: '#013E78', color: 'white', fontWeight: 'bold', textAlign: 'center', marginRight: '20px' }}
                title={`Weightage: ${option.weightage}`}
              />
            </div>
          ))
        ) : (
          <Typography>No options available.</Typography>
        )}
      </div>
    );
  };

  const renderQuestionInput = (question) => {
    const isUnanswered = unansweredQuestions.some(q => q.questionId === question.questionId);
    const isFirstUnanswered = question.questionId === firstUnansweredQuestionId;

    switch (question.questionType) {
      case 'multiple choice':
      case 'checkboxes':
      case 'dropdown':
        return (
          <>
            <div
              className="survey-form-form-row survey-form-option-row"
            >
              {renderOptions(question)}
            </div>
            {question.addCommentBox && renderCommentBox(question)}
            {isUnanswered && (
              <Typography color="error" style={{ fontSize: '15px', marginTop: '5px' }}>
                ! This is a required question
              </Typography>
            )}
          </>
        );
      case 'short answer':
        return (
          <>
            <div
              className="survey-form-form-group"
            >
              <input
                type="text"
                value={userResponses[question.questionId]?.response || question.userResponse || ''}
                onChange={(e) => onResponseChange(question.questionId, e.target.value)}
                placeholder="Enter your response..."
                className="survey-form-option-input"
              />
            </div>
            {question.addCommentBox && renderCommentBox(question)}
            {isUnanswered && (
              <Typography color="error" style={{ fontSize: '15px', marginTop: '5px' }}>
                ! This is a required question
              </Typography>
            )}
          </>
        );
      case 'paragraph':
        return (
          <>
            <div
              className="survey-form-form-group"
            >
              <textarea
                value={userResponses[question.questionId]?.response || question.userResponse || ''}
                onChange={(e) => onResponseChange(question.questionId, e.target.value)}
                placeholder="Enter your response..."
                className="survey-form-paragraph-input"
                style={{ width: '400px', height: '100px' }}
              />
            </div>
            {question.addCommentBox && renderCommentBox(question)}
            {isUnanswered && (
              <Typography color="error" style={{ fontSize: '15px', marginTop: '5px' }}>
                ! This is a required question
              </Typography>
            )}
          </>
        );
      case 'linear scale':
        const minValue = Number(question.min) || 1;
        const maxValue = Number(question.max) || 5;
        const range = Array.from({ length: maxValue - minValue + 1 }, (_, i) => maxValue - i);
        const selectedValue = userResponses[question.questionId]?.response;

        const emojiMap = {
          1: '😡',
          2: '😠',
          3: '😐',
          4: '😊',
          5: '😍'
        };

        return (
          <>
            <div style={{ display: 'flex', gap: '40px', alignItems: 'flex-start', width: '100%' }}>
              <div style={{ flex: 1 }}>
                <div
                  className="survey-form-linear-scale-group"
                >
                  <div className="survey-form-linear-scale-range" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <div style={{ display: 'flex', gap: '15px', width: '100%', justifyContent: 'space-around' }}>
                      {range.map((value) => (
                        <div
                          key={value}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '10px',
                            border: selectedValue === value ? '2px solid #013E7D' : '2px solid transparent',
                            borderRadius: '5px',
                            backgroundColor: selectedValue === value ? '#F5F8FA' : 'transparent',
                          }}
                          onClick={() => onResponseChange(question.questionId, value)}
                        >
                          <span style={{ fontSize: '30px' }}>
                            {emojiMap[value]}
                          </span>
                          <span style={{ marginTop: '15px', fontSize: '14px' }}>{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {question.addCommentBox && (
                <div style={{ width: '350px', marginTop: '-15px' }}>
                  {renderCommentBox(question)}
                </div>
              )}
            </div>
            {isUnanswered && (
              <Typography color="error" style={{ fontSize: '15px', marginTop: '5px' }}>
                ! This is a required question
              </Typography>
            )}
          </>
        );
      default:
        return <Typography color="error">Error: Unrecognized question type "{question.questionType}".</Typography>;
    }
  };

  return (
    <Box>
      {loading && <Typography>Loading survey details...</Typography>}
      {error && <Typography color="error">{error}</Typography>}
      {surveyData && (
        <div className="survey-details-questions-tab" style={{ backgroundColor: '#ffffff' }}>
          {(() => {
            // Create sorted sections array with display order
            const sortedSections = [...surveyData.sections].sort((a, b) => {
              const aWeightage = parseFloat(a.weightage || 0);
              const bWeightage = parseFloat(b.weightage || 0);

              // Sections with 0 weightage go to the end
              if (aWeightage === 0 && bWeightage !== 0) return 1;
              if (aWeightage !== 0 && bWeightage === 0) return -1;

              // Maintain original order for sections with same weightage category
              return 0;
            });

            // Find the display index of the current section
            const currentSection = surveyData.sections[currentSectionIndex];
            const displaySectionIndex = sortedSections.findIndex(s => s.sectionId === currentSection?.sectionId);

            return [currentSection]
              .filter(section => section)
              .map((section) => {
                let questionCounter = 0;
                return (
                  <div key={section.sectionId} className="survey-section-box">
                    <div className="add-survey-section-header">
                      <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div className="survey-details-section-title">
                          <strong style={{ marginLeft: '10px' }}>
                            {section.title}
                            {section.weightage && (
                              <span style={{ marginLeft: '8px', fontWeight: 'normal', color: '#013E7D' }}>
                                ({section.weightage}%)
                              </span>
                            )}
                          </strong>
                        </div>
                        <div className="survey-details-section-count-wrapper">
                          <span className="survey-details-section-count" style={{ marginRight: '10px' }}>
                            Section {displaySectionIndex + 1} of {surveyData.sections.length}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>

                      <div style={{ borderBottom: '1px solid #00000033', margin: '10px 0' }}></div>
                      <div className="add-survey-questions-list" id="default">
                        {section.subsections?.length > 0 ? (
                          section.subsections.map((measure, measureIndex) => (
                            <div key={measure.measureId || measureIndex} className="survey-measure-block">
                              {section.title?.trim().toLowerCase() !== 'overall feedback' && parseFloat(section.weightage || 0) !== 0 && (
                                <Typography variant="h6" style={{ marginTop: '10px', color: '#013E7D' }}>
                                  {measure.measureName}
                                </Typography>
                              )}
                              {measure.questions?.length > 0 ? (
                                measure.questions.map((q) => {
                                  questionCounter++;
                                  return (
                                    <div
                                      key={q.questionId}
                                      className="add-survey-question-box"
                                      id={`question-${q.questionId}`}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                                        <span>{`${displaySectionIndex + 1}.${questionCounter}.`}</span>
                                        <Typography style={{ flex: 1 }}>
                                          {q.text}
                                          <Typography component="span" color="error" style={{ fontSize: '20px' }}>*</Typography>
                                        </Typography>
                                      </div>
                                      {renderQuestionInput(q)}
                                      <div style={{ borderBottom: '1px solid #00000033', margin: '5px 0' }}></div>
                                    </div>
                                  );
                                })
                              ) : (
                                <Typography>No questions in this measure.</Typography>
                              )}
                            </div>
                          ))
                        ) : (
                          <Typography>No measures in this section.</Typography>
                        )}
                      </div>
                    </div>
                  </div>
                );
              });
          })()}
        </div>
      )}
    </Box>
  );
};

export default SurveyDetailsTemplate;